import { ApiError, AttemptsService, QuizzesService } from "@/client"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { QuizLanding } from "@/components/TestTaking/QuizLanding"
import { getLastQuestionIndex } from "@/lib/quizAttemptStorage"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { Alert, Stack } from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Outlet,
  createFileRoute,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"

export const Route = createFileRoute("/_layout/quizzes/$quizId")({
  component: QuizTakingPage,
})

function useSafePathname(): string {
  try {
    return useRouterState({
      select: (state) => state.location.pathname,
    })
  } catch {
    if (typeof window !== "undefined") {
      return window.location.pathname
    }
    return ""
  }
}

function QuizTakingPage() {
  const { quizId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const pathname = useSafePathname()

  const {
    data: quiz,
    isLoading: isLoadingQuiz,
    error: quizError,
  } = useQuery({
    queryKey: ["quiz", quizId],
    queryFn: () => QuizzesService.getQuiz({ quizId }),
    retry: false,
  })

  const { data: attemptData, isLoading: isLoadingAttempt } = useQuery({
    queryKey: ["quiz-current-attempt", quizId],
    queryFn: () => AttemptsService.getCurrentAttempt({ quizId }),
    enabled: Boolean(quizId),
    retry: false,
  })
  const attemptSnapshot = attemptData ?? null

  const shouldCheckCompletedAttempts = quiz?.allow_retake === false
  const { data: completedAttempts } = useQuery({
    queryKey: ["my-attempts", "quiz", quizId],
    queryFn: async () =>
      AttemptsService.getMyQuizAttempts({ quizId, limit: 1 }),
    enabled: Boolean(quizId && shouldCheckCompletedAttempts),
    retry: false,
    staleTime: 30_000,
  })

  const attemptPathPrefix = `/quizzes/${quizId}/attempt/`
  const attemptsPathPrefix = `/quizzes/${quizId}/attempts/`
  const isAttemptRoute =
    pathname.startsWith(attemptPathPrefix) ||
    pathname.startsWith(attemptsPathPrefix)

  const startAttemptMutation = useMutation({
    mutationFn: () => AttemptsService.startQuizAttempt({ quizId }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["quiz-current-attempt", quizId],
      })
      if (data?.attempt_id) {
        navigate({
          to: "/quizzes/$quizId/attempt/$attemptId/question/$questionIndex",
          params: {
            quizId,
            attemptId: data.attempt_id,
            questionIndex: "0",
          },
        })
      }
    },
  })

  const breadcrumbs = useRouteBreadcrumbs("quizzes.index", [
    { label: quiz?.name ?? "Quiz", isCurrent: true },
  ])

  if (isAttemptRoute) {
    return <Outlet />
  }

  if (isLoadingQuiz) {
    return (
      <PageShell title="Quiz" breadcrumbs={breadcrumbs}>
        <PageSkeleton variant="detail" rows={4} />
      </PageShell>
    )
  }

  if (quizError) {
    const isAccessDenied =
      quizError instanceof ApiError &&
      (quizError.status === 403 || quizError.status === 404)

    return (
      <PageShell title="Quiz" breadcrumbs={breadcrumbs}>
        {isAccessDenied ? (
          <div data-testid="access-denied">
            <Alert.Root status="warning">
              <Alert.Title>Access denied</Alert.Title>
              <Alert.Description>
                You don't have permission to access this quiz.
              </Alert.Description>
            </Alert.Root>
          </div>
        ) : (
          <Alert.Root status="error">
            <Alert.Title>Unable to load quiz</Alert.Title>
            <Alert.Description>
              {(quizError as Error).message || "Please try again later."}
            </Alert.Description>
          </Alert.Root>
        )}
      </PageShell>
    )
  }

  if (!quiz) {
    return null
  }

  const cutoffDate = quiz?.cutoff_date ? new Date(quiz.cutoff_date) : null
  const cutoffIsValid =
    Boolean(cutoffDate) && !Number.isNaN(cutoffDate?.getTime())
  const isPastCutoff = Boolean(
    cutoffIsValid && cutoffDate && cutoffDate.getTime() <= Date.now(),
  )
  const hasCompletedAttempt = Boolean(completedAttempts?.data?.length)
  const startDisabledReason = (() => {
    if (isPastCutoff) {
      const formatted = cutoffDate?.toLocaleString()
      return {
        title: "Quiz closed",
        description: formatted
          ? `The cutoff date passed on ${formatted}. You can no longer start or submit attempts.`
          : "The cutoff date has passed. You can no longer start or submit attempts.",
      }
    }
    if (
      shouldCheckCompletedAttempts &&
      !attemptSnapshot &&
      hasCompletedAttempt
    ) {
      return {
        title: "Retakes are not allowed",
        description:
          "You already completed this quiz and the instructor disabled retakes.",
      }
    }
    return null
  })()
  const startError =
    startAttemptMutation.isError && startAttemptMutation.error
      ? (() => {
          const err = startAttemptMutation.error
          if (err instanceof ApiError) {
            const detail =
              err.body && typeof err.body === "object" && "detail" in err.body
                ? String(
                    (err.body as { detail?: unknown }).detail ?? err.message,
                  )
                : err.message
            return detail
          }
          return (err as Error).message
        })()
      : null

  const handleStartOrResume = () => {
    if (startDisabledReason) {
      return
    }
    if (attemptSnapshot?.attempt_id) {
      const questionCount = quiz.questions?.length ?? 0
      const storedIndex = getLastQuestionIndex(attemptSnapshot.attempt_id)
      const normalizedIndex =
        storedIndex !== null && questionCount > 0
          ? Math.min(Math.max(storedIndex, 0), questionCount - 1)
          : 0
      navigate({
        to: "/quizzes/$quizId/attempt/$attemptId/question/$questionIndex",
        params: {
          quizId,
          attemptId: attemptSnapshot.attempt_id,
          questionIndex: normalizedIndex.toString(),
        },
      })
      return
    }
    startAttemptMutation.mutate()
  }

  return (
    <PageShell
      title={quiz.name}
      breadcrumbs={breadcrumbs}
      description="Review quiz details and resume your latest attempt."
    >
      <Stack gap={8}>
        <QuizLanding
          quizId={quizId}
          quiz={{
            name: quiz.name,
            time_limit_minutes: quiz.time_limit_minutes,
            allow_retake: Boolean(quiz.allow_retake),
            cutoff_date: quiz.cutoff_date ?? null,
            question_count: quiz.questions?.length ?? 0,
          }}
          hasActiveAttempt={Boolean(attemptSnapshot)}
          timeRemainingSeconds={attemptSnapshot?.time_remaining_seconds}
          startedAt={attemptSnapshot?.started_at}
          onStart={handleStartOrResume}
          isStarting={startAttemptMutation.isPending || isLoadingAttempt}
          isStartDisabled={Boolean(startDisabledReason)}
          disabledReason={startDisabledReason}
          actionError={startError}
        />
      </Stack>
    </PageShell>
  )
}
